import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { useCalendarTimezones } from '@/common/hooks/queries/useCalendarTimezones'
import { useProductCategories } from '@/common/hooks/queries/useProductCategories'
import useDebounce from '@/common/hooks/useDebounce'
import { CalendarTimezoneTransformer } from '@/common/services/calendarTimezoneTransformer'
import { Mapper } from '@/common/services/mapper'
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { UserData } from '@/common/types/users'
import { updateGeneralPoliticsSchema, UpdateGeneralPoliticsValidationSchema } from '@/features/establishments/schemas/politics/updateGeneralPolitics.schema'
import { useUsers } from '@/features/users/hooks/useUsers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useUpdateEstablishmentGeneralPolitics } from '../hooks/useUpdateEstablishmentGeneralPolitics'
import { FormTransformers } from '@/common/utils/formTransformers'
import Checkbox from '@/common/components/Checkbox'

interface UpdateGeneralPoliticsModalProps {
  establishmentCompany?: EstablishmentCompanyData
  isFetching?: boolean
}

export const UpdateGeneralPoliticsModal = ({ establishmentCompany, isFetching }: UpdateGeneralPoliticsModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchProductCategories, setSearchProductCategories] = useDebounce('');
  const [searchUsers, setSearchUsers] = useDebounce('');
  const [searchTimezone, setSearchTimezone] = useDebounce('')

  const {
    data: productCategoriesOptions,
    isPending: isProductCategoriesPending
  } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const {
    data: usersOptions,
    isPending: isUsersPending,
  } = useUsers({
    filters: { search: searchUsers },
    mapper: Mapper.mapToOptions<UserData>({ valueFieldName: 'id', labelFieldName: 'full_name' }),
  });

  const {
    data: timezonesOptions,
    isPending: isTimezonesPending,
  } = useCalendarTimezones({
    filters: { search: searchTimezone },
    mapper: Mapper.mapWithTransformer({ transformer: CalendarTimezoneTransformer.optionsTransformer() }),
  })

  const {
    control,
    handleSubmit,
    reset,
    register,
    setError,
    formState: { errors },
  } = useForm<UpdateGeneralPoliticsValidationSchema>({
    resolver: zodResolver(updateGeneralPoliticsSchema),
  })

  const {
    mutate: updateGeneralPolitics,
    isPending: isUpdateGeneralPoliticsPending
  } = useUpdateEstablishmentGeneralPolitics({
    establishmentCompany: establishmentCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] })
      setIsOpen(false)
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdateGeneralPoliticsValidationSchema> = (form) => updateGeneralPolitics(form)

  useEffect(() => {
    if (isOpen) {
      reset({
        product_categories: establishmentCompany?.company_product_categories
          ? Mapper.mapToOptions({ valueFieldName: 'id', labelFieldName: 'display_name' })(
            establishmentCompany?.company_product_categories.map(({ product_category }) => product_category)
          )
          : [],
        commercial_manager: {
          label: establishmentCompany?.commercial_manager?.full_name,
          value: establishmentCompany?.commercial_manager?.id,
        },
        time_zone_id: establishmentCompany?.time_zone ? CalendarTimezoneTransformer.optionsTransformer()([establishmentCompany.time_zone])[0] : undefined,
      })
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_bill_details_modal"
          operationKlassToCheckPermission="Admin::Establishment::Companies::UpdatePoliciesGeneralData"
          className="h-[2.125rem] text-sm text-white-800"
          disabled={!!isFetching}
        >
          Editar
        </Button>
      }
      title="Editando Geral"
      dataTestid="update_general_politics"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700 rounded-br-lg"
      cancelButtonClassName="bg-white-100 rounded-bl-lg"
      disabled={isUpdateGeneralPoliticsPending}
      confirmButtonIsLoading={isUpdateGeneralPoliticsPending}
      bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="product_categories"
              label="Serviços permitidos"
              isMulti
              onInputChange={setSearchProductCategories}
              options={productCategoriesOptions}
              isLoading={isProductCategoriesPending}
              isDisabled={isUpdateGeneralPoliticsPending}
              errorMessage={errors.product_categories?.message || undefined}
            />

            <SelectController
              control={control}
              name="commercial_manager"
              label="Gestor responsável"
              errorMessage={errors?.commercial_manager?.message || undefined}
              onInputChange={setSearchUsers}
              options={usersOptions || []}
              isLoading={isUsersPending}
              isDisabled={isUpdateGeneralPoliticsPending}
            />

            <SelectController
              control={control}
              name="time_zone_id"
              label="Fuso horário da empresa"
              errorMessage={errors?.time_zone_id?.message}
              options={timezonesOptions}
              onInputChange={setSearchTimezone}
              isDisabled={isUpdateGeneralPoliticsPending}
              isLoading={isTimezonesPending}
            />
            <Checkbox
              label="Ativar estorno via POS"
              className="text-white-800"
              {...register('allows_refunds_via_pos')}
              defaultChecked={establishmentCompany?.allows_refunds_via_pos}
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
